/**
 * Toast notification utilities
 *
 * All user-initiated actions that trigger wallet interactions or API requests
 * MUST show success/failure feedback using these toast functions.
 *
 * @example
 * // For wallet actions
 * showToast.promise(signTransaction(), {
 *   loading: 'Signing transaction...',
 *   success: 'Transaction signed!',
 *   error: 'Failed to sign transaction',
 * });
 *
 * // For simple feedback
 * showToast.success('Wallets linked successfully');
 * showToast.error('Failed to connect wallet');
 */
import { toast } from 'sonner';

/**
 * Toast helper functions with consistent styling and behavior
 */
export const showToast = {
  /**
   * Show a success toast
   * Use after successful wallet actions or API requests
   */
  success: (message: string, description?: string) => {
    toast.success(message, {
      description,
      duration: 4000,
    });
  },

  /**
   * Show an error toast
   * Use when wallet actions or API requests fail
   */
  error: (message: string, description?: string) => {
    toast.error(message, {
      description,
      duration: 6000, // Longer duration for errors so users can read
    });
  },

  /**
   * Show a warning toast
   * Use for non-critical issues or important notices
   */
  warning: (message: string, description?: string) => {
    toast.warning(message, {
      description,
      duration: 5000,
    });
  },

  /**
   * Show an info toast
   * Use for neutral information
   */
  info: (message: string, description?: string) => {
    toast.info(message, {
      description,
      duration: 4000,
    });
  },

  /**
   * Show a loading toast that can be updated
   * Returns the toast ID for updating
   */
  loading: (message: string) => {
    return toast.loading(message);
  },

  /**
   * Dismiss a specific toast by ID
   */
  dismiss: (toastId: string | number) => {
    toast.dismiss(toastId);
  },

  /**
   * Promise-based toast for async operations
   * Automatically shows loading, success, and error states
   *
   * @example
   * showToast.promise(linkWallets(), {
   *   loading: 'Linking wallets...',
   *   success: 'Wallets linked successfully!',
   *   error: (err) => err.message || 'Failed to link wallets',
   * });
   */
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    }
  ) => {
    return toast.promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
    });
  },
};

/**
 * Common toast messages for wallet actions
 * Use these for consistency across the app
 */
export const TOAST_MESSAGES = {
  // Wallet connection
  WALLET_CONNECTED: 'Wallet connected',
  WALLET_DISCONNECTED: 'Wallet disconnected',
  WALLET_CONNECTION_FAILED: 'Failed to connect wallet',

  // Wallet linking
  WALLETS_LINKED: 'Wallets linked successfully',
  WALLETS_UNLINKED: 'Wallet unlinked successfully',
  WALLET_LINK_FAILED: 'Failed to link wallets',
  WALLET_UNLINK_FAILED: 'Failed to unlink wallet',

  // Authentication
  SIGNED_IN: 'Signed in successfully',
  SIGNED_OUT: 'Signed out',
  SIGN_IN_FAILED: 'Failed to sign in',
  AUTH_REQUIRED: 'Please sign in to continue',
  LOGGED_OUT: 'Logged out successfully',
  LOGOUT_FAILED: 'Failed to log out',

  // Signatures
  SIGNATURE_REJECTED: 'Signature request was rejected',
  SIGNATURE_FAILED: 'Failed to sign message',

  // Staking transactions
  STAKE_SUCCESS: 'Tokens staked successfully',
  STAKE_FAILED: 'Failed to stake tokens',
  UNSTAKE_INITIATED: 'Cooldown started',
  UNSTAKE_FAILED: 'Failed to initiate unstake',
  COOLDOWN_CANCELLED: 'Cooldown cancelled',
  COOLDOWN_CANCEL_FAILED: 'Failed to cancel cooldown',
  WITHDRAW_SUCCESS: 'Tokens withdrawn successfully',
  WITHDRAW_FAILED: 'Failed to withdraw tokens',
  MERGE_SUCCESS: 'Positions merged successfully',
  MERGE_FAILED: 'Failed to merge positions',
  SPLIT_SUCCESS: 'Position split successfully',
  SPLIT_FAILED: 'Failed to split position',

  // Claim transactions
  CLAIM_PHNM_SUCCESS: 'PHNM claimed successfully',
  CLAIM_PHNM_FAILED: 'Failed to claim PHNM',
  CLAIM_USDT_SUCCESS: 'USDT claimed successfully',
  CLAIM_USDT_FAILED: 'Failed to claim USDT',
  CLAIM_CHIP_SUCCESS: 'CHIP claimed successfully',
  CLAIM_CHIP_FAILED: 'Failed to claim CHIP',
  REGISTRATION_FAILED: 'Failed to register for CHIP claims',

  // Network
  NETWORK_SWITCH_SUCCESS: 'Network switched',
  NETWORK_SWITCH_FAILED: 'Failed to switch network',
  WRONG_NETWORK: 'Please switch to the correct network',

  // Wallet drawer - Send
  SEND_SUCCESS: 'Tokens sent successfully',
  SEND_FAILED: 'Failed to send tokens',

  // Wallet drawer - NFT Transfer
  TRANSFER_SUCCESS: 'Position transferred successfully',
  TRANSFER_FAILED: 'Failed to transfer position',

  // Wallet drawer - Copy
  ADDRESS_COPIED: 'Address copied to clipboard',

  // Wallet disconnect
  WALLET_DISCONNECT_UNSUPPORTED: 'Cannot disconnect from wallet',
  WALLET_DISCONNECT_FAILED: 'Failed to disconnect wallet',

  // Generic
  OPERATION_FAILED: 'Operation failed. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  TRANSACTION_PENDING: 'Transaction submitted',
  TRANSACTION_CONFIRMED: 'Transaction confirmed',
  TRANSACTION_FAILED: 'Transaction failed',
} as const;
